// ——————— GEHELE FILE START ———————

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Divider,
  Autocomplete
} from "@mui/material";
import { useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar.tsx";
import { useAuth } from "../context/AuthContext.tsx";

const API_BASE = process.env.REACT_APP_API_URL + "/api";

interface VerpleegkundigeData {
  name: string;
  minDagen: number;
  maxDagen: number;
  opmerkingen: string;
  aantalVroeges: number;
  aantalLates: number;
  gewensteVroeges: number;
  gewensteLates: number;
  submitted: boolean;
}

const OverzichtVerpleegkundigen: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const fetchedRef = useRef(false);

  const { year, month } = useParams(); // /overzicht-verpleegkundigen/2026/1

  const [verpleegkundigen, setVerpleegkundigen] = useState<
    { id: number; name: string; submitted?: boolean }[]
  >([]);

  const [verpleegkundigeData, setVerpleegkundigeData] = useState<Record<string, VerpleegkundigeData>>({});
  const [selectedNurses, setSelectedNurses] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<"ingediend" | "niet-ingediend" | null>(null);

  // --------------------------------------------------
  // FETCH
  // --------------------------------------------------
  useEffect(() => {
    const fetchNurses = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token || !year || !month) return;

      const res = await fetch(`${API_BASE}/full-nurse-overview/${year}/${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        console.error("Failed to fetch nurses:", await res.text());
        return;
      }

      const data = await res.json();
      if (!Array.isArray(data)) return;

      const nurseList = data.map((n: any) => ({
        id: n.id,
        name: n.name,
        submitted: n.submitted
      }));

      setVerpleegkundigen(nurseList);
      setSelectedNurses(nurseList.map(n => n.name));

      const mapped: Record<string, VerpleegkundigeData> = {};

      data.forEach((n: any) => {
        let v = 0;
        let l = 0;

        (n.preferences || []).forEach((p: any) => {
          if (p.preference === "vroege") v++;
          else if (p.preference === "late") l++;
          else if (p.preference === "vroege-late") { v++; l++; }
        });

        mapped[n.name] = {
          name: n.name,
          minDagen: n.minDays ?? 0,
          maxDagen: n.maxDays ?? 0,
          opmerkingen: n.notes ?? "",
          aantalVroeges: v,
          aantalLates: l,
          gewensteVroeges: v,
          gewensteLates: l,
          submitted: !!n.submitted
        };
      });

      setVerpleegkundigeData(mapped);
    };

    if (isAuthenticated && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchNurses();
    }
  }, [isAuthenticated, year, month]);

  if (!isAuthenticated) return null;

  // --------------------------------------------------
  // FILTER
  // --------------------------------------------------
  const filteredNames = selectedNurses.filter((name) => {
    const d = verpleegkundigeData[name];
    if (!d) return false;
    if (filterType === "ingediend") return d.submitted;
    if (filterType === "niet-ingediend") return !d.submitted;
    return true;
  });

  // --------------------------------------------------
  // UI
  // --------------------------------------------------
  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar open={true} onClose={() => {}} />

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Container maxWidth="lg">
          <Paper sx={{ p: 4, mt: 2 }}>

            <Typography variant="h6" sx={{ mb: 3, color: "#4aa8b4" }}>
              {month}/{year}
            </Typography>

            <Autocomplete
              multiple
              options={verpleegkundigen}
              getOptionLabel={(o) => o.name}
              value={verpleegkundigen.filter(v => selectedNurses.includes(v.name))}
              onChange={(_, value) => setSelectedNurses(value.map(v => v.name))}
              renderInput={(params) => (
                <TextField {...params} placeholder="Zoek verpleegkundigen..." />
              )}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
              <Button
                variant={filterType === "ingediend" ? "contained" : "outlined"}
                onClick={() => setFilterType(filterType === "ingediend" ? null : "ingediend")}
              >
                Ingediende vpk.
              </Button>
              <Button
                variant={filterType === "niet-ingediend" ? "contained" : "outlined"}
                onClick={() => setFilterType(filterType === "niet-ingediend" ? null : "niet-ingediend")}
              >
                Niet ingediende vpk.
              </Button>
            </Box>

            {filteredNames.map((name) => {
              const d = verpleegkundigeData[name];
              if (!d) return null;

              return (
                <Box key={name} sx={{ mb: 4 }}>
                  <Divider sx={{ mb: 3 }} />
                  <Typography variant="h6">{name} {d.submitted ? "✓" : ""}</Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Min dagen"
                        type="number"
                        value={d.minDagen}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Max dagen"
                        type="number"
                        value={d.maxDagen}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Opmerkingen"
                        value={d.opmerkingen}
                        multiline
                        rows={2}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}

          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default OverzichtVerpleegkundigen;

// ——————— GEHELE FILE EINDE ———————
